// صفحه امضا (AC-DOC-041.1، US-DOC-042): رسم با Pointer Events، ذخیره مختصات نرمال‌شده، فشار قلم و زمان هر نقطه (C-AGENT-497 حداقل
// شواهد) و تصویر PNG. مرورگر بدون Canvas (مثلاً jsdom) فقط مسیر قلم را نگه می‌دارد و تصویر شفاف یک‌پیکسلی می‌فرستد.
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from '@phoenix/i18n';
import { useEffect, useRef, useState, type PointerEvent } from 'react';

export interface SignaturePoint {
  x: number;
  y: number;
  p: number;
  t: number;
}

export interface SignatureValue {
  image: string;
  strokes: SignaturePoint[][];
}

/** PNG شفاف ۱×۱ برای محیط بدون Canvas. */
export const BLANK_PNG =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==';

export interface SignaturePadProps {
  label: string;
  onChange: (value: SignatureValue | null) => void;
  testId: string;
  height?: number;
}

export function SignaturePad({ label, onChange, testId, height = 160 }: SignaturePadProps) {
  const { t } = useTranslation('doc');
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const strokes = useRef<SignaturePoint[][]>([]);
  const started = useRef<number>(0);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const element = canvas.current;
    if (!element) {
      return;
    }
    const context = safeContext(element);
    if (context) {
      context.lineWidth = 2;
      context.lineCap = 'round';
      context.strokeStyle = '#111';
    }
  }, []);

  const point = (event: PointerEvent<HTMLCanvasElement>): SignaturePoint => {
    const rect = event.currentTarget.getBoundingClientRect();
    const width = rect.width || 1;
    const heightPx = rect.height || 1;
    return {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / width)),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / heightPx)),
      p: event.pressure || 0.5,
      t: Math.round(performance.now() - started.current),
    };
  };

  const emit = () => {
    const element = canvas.current;
    let image = BLANK_PNG;
    if (element && safeContext(element)) {
      try {
        const data = element.toDataURL('image/png');
        image = data.startsWith('data:image/png') ? data : BLANK_PNG;
      } catch {
        image = BLANK_PNG;
      }
    }
    onChange(strokes.current.length > 0 ? { image, strokes: strokes.current } : null);
  };

  const onDown = (event: PointerEvent<HTMLCanvasElement>) => {
    if (strokes.current.length === 0) {
      started.current = performance.now();
    }
    drawing.current = true;
    const first = point(event);
    strokes.current = [...strokes.current, [first]];
    const context = canvas.current ? safeContext(canvas.current) : null;
    if (context && canvas.current) {
      context.beginPath();
      context.moveTo(first.x * canvas.current.width, first.y * canvas.current.height);
    }
  };

  const onMove = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) {
      return;
    }
    const next = point(event);
    const current = strokes.current[strokes.current.length - 1];
    current?.push(next);
    const context = canvas.current ? safeContext(canvas.current) : null;
    if (context && canvas.current) {
      context.lineWidth = 1 + next.p * 2;
      context.lineTo(next.x * canvas.current.width, next.y * canvas.current.height);
      context.stroke();
    }
  };

  const onUp = () => {
    if (!drawing.current) {
      return;
    }
    drawing.current = false;
    setHasInk(true);
    emit();
  };

  const clear = () => {
    strokes.current = [];
    setHasInk(false);
    const element = canvas.current;
    const context = element ? safeContext(element) : null;
    if (context && element) {
      context.clearRect(0, 0, element.width, element.height);
    }
    onChange(null);
  };

  return (
    <Stack spacing={0.5}>
      <Typography variant="subtitle2">{label}</Typography>
      <Box
        sx={{ border: 1, borderColor: hasInk ? 'primary.main' : 'divider', borderRadius: 1, bgcolor: 'background.paper', touchAction: 'none' }}
      >
        <canvas
          ref={canvas}
          width={600}
          height={height * 2}
          style={{ width: '100%', height, display: 'block', touchAction: 'none' }}
          aria-label={label}
          role="img"
          data-testid={testId}
          data-signed={hasInk ? 'true' : 'false'}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerLeave={onUp}
        />
      </Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          {t('doc:signature.hint')}
        </Typography>
        <Button size="small" onClick={clear} disabled={!hasInk} data-testid={`${testId}-clear`}>
          {t('doc:signature.clear')}
        </Button>
      </Stack>
    </Stack>
  );
}

function safeContext(element: HTMLCanvasElement): CanvasRenderingContext2D | null {
  try {
    return element.getContext('2d');
  } catch {
    return null;
  }
}
